/** Max contact lists a single campaign can target. Mirrors MAX_CAMPAIGN_CONTACT_LISTS on the backend. */
export const MAX_CAMPAIGN_CONTACT_LISTS = 10;

/**
 * Converts seconds to a human-readable format of time remaining
 * @param seconds The number of seconds to convert
 * @returns A string in the format "X days and Y hours remaining" or "X minutes remaining" for times less than an hour
 */
export function formatSecondsToReadableTime(seconds: number): string {
  // Handle negative values
  if (seconds < 0) {
    return "Invalid input: seconds cannot be negative";
  }

  // Define conversion constants
  const secondsInMinute = 60;
  const secondsInHour = 3600;
  const secondsInDay = 24 * secondsInHour;

  // Calculate days, hours, and minutes
  const days = Math.floor(seconds / secondsInDay);
  const remainingSecondsAfterDays = seconds % secondsInDay;
  const hours = Math.floor(remainingSecondsAfterDays / secondsInHour);
  const minutes = Math.floor((seconds % secondsInHour) / secondsInMinute);

  // Build the result string
  let result = "";

  // If less than an hour, show minutes
  if (days === 0 && hours === 0) {
    return `${minutes} min remaining`;
  }

  if (days > 0) {
    result += `${days} day${days === 1 ? "" : "s"}`;

    if (hours > 0) {
      result += ` ${hours} hr${hours === 1 ? "" : "s"}`;
    }
  } else {
    // Only hours, no days
    result += `${hours} hr${hours === 1 ? "" : "s"}`;
  }

  result += " remaining";

  return result;
}

/**
 * Extracts the domain TLD from a domain name
 * @param domain The domain name to extract domain TLD from
 * @returns The domain TLD without the leading dot, or null if invalid
 */
export function extractDomainTLDFromDomain(domain: string): string | null {
  if (!domain || typeof domain !== 'string') {
    return null;
  }

  // Remove protocol if present
  const cleanDomain = domain.replace(/^https?:\/\//, '').toLowerCase().trim();

  // Basic domain validation
  if (!cleanDomain || cleanDomain.includes(' ') || cleanDomain.startsWith('.') || cleanDomain.endsWith('.')) {
    return null;
  }

  // Split by dots and get the last part
  const parts = cleanDomain.split('.');
  if (parts.length < 2) {
    return null;
  }

  const tld = parts[parts.length - 1];

  // Basic domain TLD validation (only letters, numbers, and hyphens)
  if (!/^[a-z0-9-]+$/.test(tld) || tld.length < 2 || tld.length > 63) {
    return null;
  }

  return tld;
}

/**
 * Validates if a domain format is correct
 * @param domain The domain to validate
 * @returns true if domain format is valid
 */
export function isValidDomainFormat(domain: string): boolean {
  if (!domain || typeof domain !== 'string') {
    return false;
  }

  // Remove protocol if present
  const cleanDomain = domain.replace(/^https?:\/\//, '').toLowerCase().trim();

  // Basic checks
  if (!cleanDomain || cleanDomain.includes(' ') || cleanDomain.startsWith('.') || cleanDomain.endsWith('.')) {
    return false;
  }

  // Check for valid domain pattern
  const domainPattern = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/;
  if (!domainPattern.test(cleanDomain)) {
    return false;
  }

  // Must have at least one dot
  if (!cleanDomain.includes('.')) {
    return false;
  }

  // Each part should not exceed 63 characters
  const parts = cleanDomain.split('.');
  for (const part of parts) {
    if (part.length === 0 || part.length > 63) {
      return false;
    }
  }

  return true;
}

export const getExportFilename = (
  email: string,
  tableName: string
) => {
  const env =
    process.env.REACT_APP_BACKEND === "https://api.deliveryman.ai"
      ? "PROD"
      : process.env.REACT_APP_BACKEND ===
        "https://staging.api.deliveryman.ai"
      ? "STAGING"
      : "DEV";

  return `${email}_${tableName}_${env}.csv`;
};
